import { HttpEventType } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, tap, Subscription, catchError, EMPTY } from 'rxjs';
import { HttpService } from 'src/app/services/http.service';
import HTTP_STATUS_CODES from '../../enums/HttpStatusCodesEnum';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { LoaderComponent } from '../../loader/loader.component';
import { BaseResponseDto, BaseResponseStatus } from '../../models/BaseResponseDto.model';

@Component({
  selector: 'terra-file-import-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatProgressBarModule,
    MatIconModule,
    MatButtonModule,
    LoaderComponent
  ],
  templateUrl: './file-import-upload.component.html',
  styleUrl: './file-import-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileImportUploadComponentimplements implements OnInit, AfterViewInit, OnDestroy {
  public isChanged = false;
  public typeHint = '';
  public fileToUpload: File = null;
  private submitImportSubscription: Subscription;

  @Input()
  uploadEndpoint;

  @Input()
  uploadButtonText = '+ Upload';

  @Input()
  supportedExtensions: string = '.xlsx';

  @Input() submitImport: Observable<void> = new Observable<void>();

  @Output() onSuccess: EventEmitter<any> = new EventEmitter<any>();
  @Output() onError: EventEmitter<any> = new EventEmitter<any>();
  @Output() disableSubmitted: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() isProcessing: EventEmitter<boolean> = new EventEmitter<boolean>(false);

  public fileTypeNotSupported = signal<boolean>(false)
  public isDocumentSelected = signal<boolean>(false);
  public showFileUploadError = signal<boolean>(false);
  public showFileLoader = signal<boolean>(false);
  public uploadLoader = signal<boolean>(false);
  public showValidationError = signal<string[]>(null);
  public displayVirusDetectedError = false;
  uploadProgress = 0;

  constructor(private http: HttpService) {

  }
  ngOnDestroy(): void {
    if (this.submitImportSubscription) {
      this.submitImportSubscription.unsubscribe();
    }
  }

  ngOnInit() { }

  ngAfterViewInit(): void {
    this.submitImportSubscription = this.submitImport.subscribe(_ => this.importFile())
  }

  clearFile() {
    this.isChanged = true;
    // Clear the file but leave the selected file type unchanged
    this.isDocumentSelected.set(false);
    this.showFileUploadError.set(false);
    this.fileTypeNotSupported.set(false);
    this.showValidationError.set(null);
    this.displayVirusDetectedError = false;
    this.fileToUpload = null;
    this.disableSubmitted.emit(true);
  }

  importFile() {
    if (!this.fileToUpload || !this.fileToUpload.name) {
      return;
    }
    this.isProcessing.emit(true);
    this.showFileLoader.set(true);
    this.fileTypeNotSupported.set(false);
    this.showFileUploadError.set(false);
    this.displayVirusDetectedError = false;
    this.uploadProgress = 0;

    this.http.uploadFile(this.uploadEndpoint, this.fileToUpload).pipe(
      tap(result => {
        if (result.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round((100 * result.loaded) / result.total);
          if (this.uploadProgress >= 100) {
            this.uploadLoader.set(true);
          }
        } else if (result.type === HttpEventType.Response) {
          this.showFileLoader.set(false);
          this.uploadLoader.set(false);
          this.onSuccess.emit(result);
          this.isProcessing.emit(false);
        }
      }),
      catchError(error => {
        this.isProcessing.emit(false);
        this.uploadLoader.set(false);
        this.displayErrorMessage(error.status, error.error);
        return EMPTY;
      })
    ).subscribe();
  }

  fileChange(event) {
    this.isChanged = true;
    this.showFileLoader.set(false);
    const fileList: FileList = event.target.files;
    if (fileList.length > 0) {
      this.fileToUpload = fileList[0];
      this.isDocumentSelected.set(true);
      this.disableSubmitted.emit(false);
    }
  }

  displayErrorMessage(httpError: HTTP_STATUS_CODES, error: BaseResponseDto) {
    switch (httpError) {
      case HTTP_STATUS_CODES.UNSUPPORTED_MEDIA_TYPE:
        this.fileTypeNotSupported.set(true);
        break;
      default:
        if (!!error) {
          this.displayErrorMessageFromServer(error);
        }
        else {
          this.showFileUploadError.set(true);
        }
        break;
    }
    this.showFileLoader.set(false);
  }

  displayErrorMessageFromServer(error: BaseResponseDto) {
    switch (error.responseStatus) {
      case BaseResponseStatus.Unauthorized:
        this.onError.emit(error);
        break;
      case BaseResponseStatus.ValidationError:
        this.showValidationError.set(error.responseMessage.split('|'));
        break;
      default:
        this.onError.emit(error);
        this.showFileUploadError.set(true);
        break;
    }
  }
}

